import * as React from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, X, Bell, BellRing, Sparkles, Sun, Moon, Monitor, LogOut, User as UserIcon,
  Menu, ChevronDown, CheckCheck, Trash2, ExternalLink,
  ChevronRight, Compass, Download,
} from "lucide-react";
import { toast } from "sonner";
import { cn, relativeTime, useDevice } from "@/lib/utils";
import { useTheme } from "@/contexts/ThemeContext";
import { useAuth } from "@/contexts/AuthContext";
import { useTour } from "@/tour/TourContext";
import { useBranding, useLogoUrl } from "@/contexts/BrandingContext";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import {
  Popover, PopoverTrigger, PopoverContent,
} from "@/components/ui/popover";
import { ConfirmDialog } from "@/components/common/confirm-dialog";
import { getUpdateStatus, openStore } from "@/lib/app-version";
import { useOffline } from "@/offline/OfflineProvider";
import { SyncStatusIndicator } from "./SyncStatusIndicator";
import { ROLE_BASE, type Role } from "./nav-config";
import { HeaderSearch } from "./HeaderSearch";
import { Capacitor } from "@capacitor/core";
import type { Notification } from "@/types/api";

/**
 * iOS WKWebView doesn't deliver Radix's DropdownMenuTrigger pointer events, so
 * the header menus never open there — even though plain-onClick buttons (the
 * hamburger / search) and the controlled Sheet work fine. We drive the open
 * state ourselves and, on iOS only, toggle it from the trigger's onClick (which
 * DOES fire). On other platforms Radix's own handling sets onOpenChange and we
 * omit the onClick to avoid double-toggling.
 */
function useTapDropdown() {
  const [open, setOpen] = React.useState(false);
  const root = { open, onOpenChange: setOpen, modal: false as const };
  const triggerTap =
    Capacitor.getPlatform() === "ios" ? { onClick: () => setOpen((o) => !o) } : {};
  return { root, triggerTap };
}

interface NotificationsHandle {
  unreadCount: number;
  latest: Notification[];
  latestLoading: boolean;
  markAsRead: (id: number) => void;
  markAllAsRead: () => void;
  deleteOne: (id: number) => void;
  clearAll: () => void;
  markAllPending: boolean;
  clearAllPending: boolean;
}

interface HeaderProps {
  /** Open the AI quick chat (or navigate to /ai page) */
  onAskAi?: () => void;
  onOpenMobileMenu?: () => void;
  notifications?: NotificationsHandle;
  className?: string;
}

export function Header({
  onAskAi,
  onOpenMobileMenu,
  notifications,
  className,
}: HeaderProps) {
  const { user, logout } = useAuth();
  // Signing out clears local state, so warn hard when offline — any changes that
  // haven't synced yet can't be recovered afterwards.
  const { online, pendingCount } = useOffline();
  const offlineSignOutRisk = !online;
  const { startTour } = useTour();
  const { theme, resolvedTheme, setTheme } = useTheme();
  const { branding } = useBranding();
  const logoUrl = useLogoUrl();
  const [logoErrored, setLogoErrored] = React.useState(false);
  const [signOutOpen, setSignOutOpen] = React.useState(false);
  const [loggingOut, setLoggingOut] = React.useState(false);
  // "App updates" (native only): check the store for a newer build.
  const isNative = Capacitor.isNativePlatform();
  // Notifications: a full-screen drawer on mobile (native app OR a small-screen
  // web browser) and a Popover dropdown on larger web viewports.
  const notifAsDrawer = isNative || useDevice() === "mobile";
  // Passive update indicator: null = not checked yet, then "available" / "current".
  const [updateAvailable, setUpdateAvailable] = React.useState<boolean | null>(null);
  // On native the profile menu opens as a bottom drawer (like notifications)
  // instead of a Radix dropdown; on web it stays a dropdown.
  const [userSheetOpen, setUserSheetOpen] = React.useState(false);
  const userSwipe = React.useRef<{ y: number; t: number } | null>(null);
  // Rows keep a normal, compact height — the drawer only changes the container.
  const drawerRow = "flex w-full items-center gap-2.5 rounded-lg px-3 py-2.5 text-sm text-fg hover:bg-surface-hover transition-colors touch-target disabled:opacity-50 disabled:pointer-events-none";
  const [checkingUpdate, setCheckingUpdate] = React.useState(false);
  const checkForUpdates = React.useCallback(async () => {
    setCheckingUpdate(true);
    try {
      const status = await getUpdateStatus();
      const available = status.forceUpgrade || status.recommendUpgrade;
      setUpdateAvailable(available);
      if (available) {
        if (status.storeUrl) openStore(status.storeUrl);
        else toast.info(status.message ?? "An update is available.");
      } else {
        toast.success("You're on the latest version.");
      }
    } finally {
      setCheckingUpdate(false);
    }
  }, []);
  // Passively check once on mount (native only) so the avatar can show a dot
  // when a soft-nudge / force update is available — no tap required.
  React.useEffect(() => {
    if (!isNative) return;
    let alive = true;
    getUpdateStatus()
      .then((s) => { if (alive) setUpdateAvailable(s.forceUpgrade || s.recommendUpgrade); })
      .catch(() => { /* fail-open: no indicator */ });
    return () => { alive = false; };
  }, [isNative]);
  const [mobileSearchOpen, setMobileSearchOpen] = React.useState(false);
  // Controlled open for the header dropdowns (iOS WKWebView fix — see useTapDropdown).
  const themeMenu = useTapDropdown();
  const userMenu = useTapDropdown();
  // Notifications open in a full-screen bottom sheet (plain onClick trigger,
  // so no iOS dropdown workaround is needed for the bell).
  const [notifOpen, setNotifOpen] = React.useState(false);
  // Swipe-down-to-dismiss for the notifications sheet (touch start point +
  // whether the touch began inside a scrollable list, which must keep scrolling).
  const notifSwipe = React.useRef<{ x: number; y: number; t: number; inScrollableList: boolean } | null>(null);
  const navigate = useNavigate();
  const role = (user?.role ?? "vessel") as Role;
  const base = ROLE_BASE[role];
  // Reset broken-image flag when the logo URL changes so a new tenant's logo gets a fresh attempt.

  React.useEffect(() => { setLogoErrored(false); }, [logoUrl]);
  const showCompanyLogo = !!logoUrl && !logoErrored;
  const companyName = branding.companyName;

  const initials = `${user?.firstName?.[0] ?? ""}${user?.lastName?.[0] ?? ""}`.toUpperCase() || "U";
  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ") || "Account";

  const handleAskAi = () => {
    if (onAskAi) onAskAi();
    else navigate(`${base}/ai`);
  };

  // Notifications bell — shared between the web popover trigger and the native
  // sheet button. Only the wrapper differs (dropdown on web, drawer on native).
  const notifAria = `Notifications${(notifications?.unreadCount ?? 0) > 0 ? `, ${notifications!.unreadCount} unread` : ""}`;
  const bellClass = "relative inline-flex size-9 items-center justify-center rounded-md text-fg-muted hover:text-fg hover:bg-surface-hover touch-target";
  const bellInner = (
    <>
      <Bell className="size-4" />
      {(notifications?.unreadCount ?? 0) > 0 ? (
        <span className="absolute top-1.5 right-1.5 inline-flex min-w-3.5 h-3.5 items-center justify-center rounded-full bg-danger text-[9px] font-semibold text-danger-fg px-1 leading-none">
          {notifications!.unreadCount > 9 ? "9+" : notifications!.unreadCount}
        </span>
      ) : null}
    </>
  );

  // The notification panel body — header actions + list + footer. `native`
  // toggles between a full-height (drawer) and capped-height (dropdown) list.
  const renderNotifPanel = (native: boolean) => (
    <>
      <div className="flex items-center justify-between gap-2 border-b border-border-subtle px-4 py-2.5">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-fg">Notifications</p>
          <p className="text-xs text-fg-muted">
            {(notifications?.unreadCount ?? 0) > 0 ? `${notifications!.unreadCount} unread` : "You're all caught up"}
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {(notifications?.unreadCount ?? 0) > 0 ? (
            <button
              onClick={() => notifications?.markAllAsRead()}
              disabled={notifications?.markAllPending}
              className="inline-flex items-center gap-1 rounded px-2 py-1.5 text-xs text-fg-muted transition-colors hover:bg-surface-hover hover:text-fg touch-target"
              title="Mark all as read"
            >
              <CheckCheck className="size-3.5" /> Read all
            </button>
          ) : null}
          {(notifications?.latest?.length ?? 0) > 0 ? (
            <button
              onClick={() => notifications?.clearAll()}
              disabled={notifications?.clearAllPending}
              className="inline-flex items-center gap-1 rounded px-2 py-1.5 text-xs text-danger transition-colors hover:bg-surface-hover hover:text-danger/80 touch-target"
              title="Clear all notifications"
            >
              <Trash2 className="size-3.5" /> Clear
            </button>
          ) : null}
        </div>
      </div>

      <div data-notif-list className={cn("overflow-y-auto overscroll-contain", native ? "flex-1" : "max-h-[min(70vh,24rem)]")}>
        {(notifications?.latest?.length ?? 0) === 0 ? (
          <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
            <Bell className="size-6 text-fg-subtle" />
            <span className="text-sm text-fg-muted">No notifications yet</span>
          </div>
        ) : (
          notifications!.latest.map((n) => (
            <div
              key={n.id}
              role="button"
              tabIndex={0}
              className={cn(
                "flex w-full items-start gap-3 border-b border-border-subtle px-4 py-3 text-left cursor-pointer transition-colors hover:bg-surface-hover",
                !n.isRead && "bg-primary/5",
              )}
              onClick={() => {
                if (!n.isRead) notifications!.markAsRead(n.id);
                if (n.link) navigate(`${base}${n.link}`);
                else navigate(`${base}/notifications`);
                setNotifOpen(false);
              }}
              onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") e.currentTarget.click(); }}
            >
              <span className="mt-0.5 shrink-0">
                {n.isRead ? <Bell className="size-4 text-fg-subtle" /> : <BellRing className="size-4 text-primary" />}
              </span>
              <span className="min-w-0 flex-1">
                <span className={cn("block text-sm", !n.isRead ? "font-medium text-fg" : "text-fg-muted")}>{n.title}</span>
                {n.message ? <span className="mt-0.5 block text-xs text-fg-muted line-clamp-2">{n.message}</span> : null}
                <span className="mt-1 block text-[11px] text-fg-subtle">{relativeTime(n.createdAt)}</span>
              </span>
              <button
                onClick={(e) => { e.stopPropagation(); notifications!.deleteOne(n.id); }}
                className="-mr-1 inline-flex size-8 shrink-0 items-center justify-center rounded-md text-fg-subtle transition-colors hover:bg-surface-hover hover:text-danger touch-target"
                aria-label="Delete notification"
              >
                <Trash2 className="size-3.5" />
              </button>
            </div>
          ))
        )}
      </div>

      <div className={cn("border-t border-border-subtle", native ? "px-5 py-2 pb-[max(env(safe-area-inset-bottom),0.5rem)]" : "px-3 py-1.5")}>
        <button
          onClick={() => { navigate(`${base}/notifications`); setNotifOpen(false); }}
          className="flex w-full items-center justify-center gap-1.5 rounded-md py-2 text-sm text-primary transition-colors hover:bg-surface-hover touch-target"
        >
          View all notifications <ChevronRight className="size-3.5" />
        </button>
      </div>
    </>
  );

  return (
    <header
      className={cn(
        "app-topbar h-20 shrink-0 flex items-center gap-2 px-3 md:px-4",
        "bg-bg/80 backdrop-blur-md border-b border-border-subtle",
        "relative z-30",
        className,
      )}
    >
      {/* Menu button — opens the slide-in nav drawer. Shown on mobile and
          tablet (where there's no persistent rail); hidden on desktop (xl+).
          Only rendered when a handler is passed, so desktop never sees it. */}
      {onOpenMobileMenu ? (
        <button
          onClick={onOpenMobileMenu}
          data-tour="mobile-nav"
          className="xl:hidden inline-flex size-9 items-center justify-center rounded-md text-fg-muted hover:text-fg hover:bg-surface-hover touch-target"
          aria-label="Open menu"
        >
          <Menu className="size-5" />
        </button>
      ) : null}

      {/* Company branding (logo + name) — tablet+ only; hidden when there's nothing to show. */}

      {(showCompanyLogo || companyName) ? (
        <div className="hidden md:flex items-center gap-2 pr-1 min-w-0 max-w-55" aria-label={companyName ?? "Company"}>
          {showCompanyLogo ? (
            <img
              src={logoUrl!}
              alt=""
              className="size-7 shrink-0 rounded object-contain bg-white border border-border-subtle"
              onError={() => setLogoErrored(true)}
            />
          ) : null}
          {companyName ? (
            <span className="text-sm font-medium text-fg truncate" title={companyName}>
              {companyName}
            </span>
          ) : null}
        </div>
      ) : null}

      {/* Inline search — desktop (xl+) only; results drop down anchored to the input. */}

      <div data-tour="search" className="hidden xl:block xl:w-96">
        <HeaderSearch className="w-full" />
      </div>

      {/* Mobile + tablet: search icon reveals the same inline search (no modal). */}

      <button
        onClick={() => setMobileSearchOpen(true)}
        className="xl:hidden inline-flex size-9 items-center justify-center rounded-md text-fg-muted hover:text-fg hover:bg-surface-hover touch-target ml-auto"
        aria-label="Search"
      >
        <Search className="size-5" />
      </button>

      {/* Reveal overlay (mobile + tablet): the inline HeaderSearch, filling the header row. */}

      {mobileSearchOpen ? (
        // Mirror the header exactly (app-topbar: same h-20 + Android status-bar
        // offset) so the revealed search row + its dropdown line up below the bar.
        <div className="app-topbar xl:hidden absolute inset-x-0 top-0 z-40 flex h-20 items-center gap-2 px-3 bg-bg/95 backdrop-blur-md border-b border-border-subtle">
          <HeaderSearch
            className="flex-1 min-w-0"
            autoFocus
            onClose={() => setMobileSearchOpen(false)}
          />
          <button
            onClick={() => setMobileSearchOpen(false)}
            className="inline-flex size-9 shrink-0 items-center justify-center rounded-md text-fg-muted hover:text-fg hover:bg-surface-hover touch-target"
            aria-label="Close search"
          >
            <X className="size-5" />
          </button>
        </div>
      ) : null}

      <div className="flex-1 hidden xl:block" />

      {/* Ask AI — Company Admin only; other roles don't have AI access. */}

      {role === "admin" ? (
        <Button
          variant="outline"
          size="sm"
          onClick={handleAskAi}
          data-tour="ask-ai"
          className="gap-1.5 hidden sm:inline-flex border-ai-border/50 text-ai-soft-fg hover:bg-ai-soft hover:border-ai-border"
        >
          <Sparkles className="size-4 text-ai-from" />
          <span className="hidden md:inline">Ask AI</span>
        </Button>
      ) : null}

      {/* Notifications — a full-screen drawer on mobile; a dropdown on wider web. */}
      {notifAsDrawer ? (
        <>
          <button onClick={() => setNotifOpen(true)} data-tour="notifications" className={bellClass} aria-label={notifAria}>
            {bellInner}
          </button>
          <Sheet open={notifOpen} onOpenChange={setNotifOpen}>
            <SheetContent
              side="bottom"
              safeTop
              showClose={false}
              // Full-screen takeover: the bottom sheet slides up over the whole
              // viewport, so drop the drawer corner-rounding and top border.
              className="h-dvh rounded-none border-t-0 p-0 flex flex-col gap-0"
              onTouchStart={(e) => {
                const t = e.touches[0];
                if (!t) return;
                // Flick-down closes the sheet — but a touch that starts inside a
                // list that can actually scroll must keep scrolling instead.
                const list = (e.target as Element).closest?.("[data-notif-list]");
                const inScrollableList = !!list && list.scrollHeight > list.clientHeight;
                notifSwipe.current = { x: t.clientX, y: t.clientY, t: Date.now(), inScrollableList };
              }}
              onTouchEnd={(e) => {
                const s = notifSwipe.current;
                notifSwipe.current = null;
                const t = e.changedTouches[0];
                if (!s || !t || s.inScrollableList) return;
                const dx = t.clientX - s.x, dy = t.clientY - s.y, dt = Date.now() - s.t;
                const flickDown = dy > 60 && Math.abs(dy) > Math.abs(dx) && dt < 400;
                if (flickDown) setNotifOpen(false);
              }}
            >
              {/* sr-only title/description for Dialog a11y (visible header is in the panel). */}
              <SheetHeader className="sr-only">
                <SheetTitle>Notifications</SheetTitle>
                <SheetDescription>Your recent notifications</SheetDescription>
              </SheetHeader>
              {/* Drawer grabber — visual cue that the sheet dismisses on flick-down. */}
              <div className="mx-auto mt-2 h-1.5 w-10 shrink-0 rounded-full bg-border" aria-hidden />
              {renderNotifPanel(true)}
            </SheetContent>
          </Sheet>
        </>
      ) : (
        <Popover open={notifOpen} onOpenChange={setNotifOpen}>
          <PopoverTrigger asChild>
            <button data-tour="notifications" className={bellClass} aria-label={notifAria}>
              {bellInner}
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" sideOffset={8} className="w-80 overflow-hidden rounded-xl p-0">
            {renderNotifPanel(false)}
          </PopoverContent>
        </Popover>
      )}

      {/* Online/offline + pending-sync status; click to sync now. */}
      <SyncStatusIndicator />

      {/* Theme toggle */}
      <DropdownMenu {...themeMenu.root}>
        <DropdownMenuTrigger asChild>
          <button
            {...themeMenu.triggerTap}
            data-tour="theme"
            className="inline-flex size-9 items-center justify-center rounded-md text-fg-muted hover:text-fg hover:bg-surface-hover touch-target"
            aria-label="Theme"
          >
            {resolvedTheme === "dark" ? <Moon className="size-4" /> : <Sun className="size-4" />}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-44">
          <DropdownMenuLabel>Appearance</DropdownMenuLabel>
          <DropdownMenuRadioGroup value={theme} onValueChange={(v) => setTheme(v as any)}>
            <DropdownMenuRadioItem value="light"><Sun className="size-4 mr-2" />Light</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="dark"><Moon className="size-4 mr-2" />Dark</DropdownMenuRadioItem>
            <DropdownMenuRadioItem value="system"><Monitor className="size-4 mr-2" />System</DropdownMenuRadioItem>
          </DropdownMenuRadioGroup>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* User menu — a Radix dropdown on web; a bottom drawer on native
       *  (matches the notifications sheet). Only the container differs; the
       *  row items keep their normal, compact height in both. */}
      {isNative ? (
        <>
          <button
            onClick={() => setUserSheetOpen(true)}
            data-tour="profile"
            aria-label={updateAvailable ? "Account menu — app update available" : "Account menu"}
            className="relative inline-flex items-center gap-1.5 rounded-md p-0.5 pr-1 hover:bg-surface-hover transition-colors touch-target cursor-pointer"
          >
            <Avatar className="size-7 border border-primary/50">
              <AvatarImage src={user?.avatarPath ?? undefined} alt={fullName} />
              <AvatarFallback className="text-primary dark:bg-primary/5">{initials}</AvatarFallback>
            </Avatar>
            {updateAvailable ? (
              // Update-available dot — ringed so it reads clearly over the header.
              <span className="absolute right-0 top-0 size-2.5 rounded-full bg-primary ring-2 ring-surface" aria-hidden />
            ) : null}
          </button>
          <Sheet open={userSheetOpen} onOpenChange={setUserSheetOpen}>
            <SheetContent
              side="bottom"
              showClose={false}
              className="gap-0 p-0 pb-[max(env(safe-area-inset-bottom),0.5rem)]"
              onTouchStart={(e) => {
                const t = e.touches[0];
                if (t) userSwipe.current = { y: t.clientY, t: Date.now() };
              }}
              onTouchEnd={(e) => {
                const s = userSwipe.current;
                userSwipe.current = null;
                const t = e.changedTouches[0];
                if (!s || !t) return;
                // Flick-down dismisses the drawer, like the notifications sheet.
                if (t.clientY - s.y > 60 && Date.now() - s.t < 400) setUserSheetOpen(false);
              }}
            >
              {/* Grabber — visual cue that the sheet dismisses on flick-down. */}
              <div className="mx-auto mt-2 h-1.5 w-10 shrink-0 rounded-full bg-border" aria-hidden />
              <SheetHeader className="px-4 pb-3 pt-3 text-left">
                <SheetTitle className="truncate text-sm font-medium">{fullName}</SheetTitle>
                <SheetDescription className="truncate text-xs text-fg-muted">{user?.email}</SheetDescription>
                <Badge variant="primary" size="sm" className="mt-1.5 w-fit capitalize border-primary/50 bg-primary/10 text-primary">
                  {role.replace("-", " ")}
                </Badge>
              </SheetHeader>
              <div className="border-t border-border p-1.5">
                <button className={drawerRow} onClick={() => { setUserSheetOpen(false); navigate(`${base}/profile`); }}>
                  <UserIcon className="size-4" />Profile
                </button>
                <button
                  className={drawerRow}
                  onClick={() => { setUserSheetOpen(false); window.setTimeout(() => startTour(), 200); }}
                >
                  <Compass className="size-4" />Product Tour
                </button>
                <button
                  className={drawerRow}
                  disabled={checkingUpdate}
                  onClick={() => { setUserSheetOpen(false); void checkForUpdates(); }}
                >
                  <Download className="size-4" />
                  <span className="flex-1 text-left">App updates</span>
                  {updateAvailable ? (
                    <span className="inline-flex items-center gap-1.5 text-xs font-medium text-primary">
                      <span className="size-1.5 rounded-full bg-primary" aria-hidden /> Available
                    </span>
                  ) : updateAvailable === false ? (
                    <span className="text-xs text-fg-muted">Up to date</span>
                  ) : null}
                </button>
              </div>
              <div className="border-t border-border p-1.5">
                <button
                  className={cn(drawerRow, "text-danger hover:bg-danger/10 hover:text-danger")}
                  onClick={() => { setUserSheetOpen(false); setSignOutOpen(true); }}
                >
                  <LogOut className="size-4" />Sign out
                </button>
              </div>
            </SheetContent>
          </Sheet>
        </>
      ) : (
        <DropdownMenu {...userMenu.root}>
          <DropdownMenuTrigger asChild>
            <button
              {...userMenu.triggerTap}
              data-tour="profile"
              className="inline-flex items-center gap-1.5 rounded-md p-0.5 pr-1 hover:bg-surface-hover transition-colors touch-target cursor-pointer"
            >
              <Avatar className="size-7 border border-primary/50">
                <AvatarImage src={user?.avatarPath ?? undefined} alt={fullName} />
                <AvatarFallback className="text-primary dark:bg-primary/5">{initials}</AvatarFallback>
              </Avatar>
              <ChevronDown className="size-3.5 text-fg-subtle hidden sm:block" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <div className="px-2 py-2">
              <p className="text-sm font-medium truncate">{fullName}</p>
              <p className="text-xs text-fg-muted truncate">{user?.email}</p>
              <Badge variant="primary" size="sm" className="mt-1.5 capitalize border-primary/50 text-primary bg-primary/10 hover:border-primary/30">
                {role.replace("-", " ")}
              </Badge>
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => navigate(`${base}/profile`)}>
              <UserIcon className="size-4" />Profile
            </DropdownMenuItem>
            <DropdownMenuItem
              // Restart the product tour. Closing the menu first lets the tour's
              // overlay highlight the shell without the dropdown obscuring it;
              // the tiny delay waits for the menu's exit animation to finish.
              onClick={() => {
                userMenu.root.onOpenChange(false);
                window.setTimeout(() => startTour(), 150);
              }}
            >
              <Compass className="size-4" />Product Tour
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              destructive
              // Open the sign-out confirmation modal — real logout runs from its onConfirm.

              onClick={() => setSignOutOpen(true)}
            >
              <LogOut className="size-4" />Sign out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      <ConfirmDialog
        open={signOutOpen}
        onOpenChange={setSignOutOpen}
        title={offlineSignOutRisk ? "Don't sign out while offline" : "Sign out of iCheck?"}
        description={
          offlineSignOutRisk
            ? "You are currently offline. To prevent data loss and ensure data integrity, please reconnect and wait for synchronization to complete before signing out."
            : "You'll be returned to the login page. Any unsaved changes on other tabs will be lost."
        }
        confirmLabel={offlineSignOutRisk ? "Sign out anyway" : "Sign out"}
        cancelLabel="Stay signed in"
        tone={offlineSignOutRisk ? ((pendingCount ?? 0) > 0 ? "danger" : "warning") : "default"}
        icon={offlineSignOutRisk ? undefined : <LogOut className="h-6 w-6 text-primary" />}
        onConfirm={async () => {
          try {
            setLoggingOut(true);
            navigate("/login", { replace: true });
            await logout();
          } finally {
            setLoggingOut(false);
            setSignOutOpen(false);
          }
        }}
        pending={loggingOut}
      />
    </header>
  );
}
